/**
 * The custom pack card search, shared by the session endpoint (Catalog) and its
 * anonymous twin (Embed Mode). Inlined by the importing paths, not registered
 * under `schemas`.
 */

import { customPackPriceExample, customPackPriceParameters, customPackPriceSchema } from './components.customPackPrice';

const filterIds = (name: string, filter: string, label: string) => ({
  name,
  in: 'query',
  required: false,
  schema: { type: 'array', maxItems: 50, items: { type: 'integer' } },
  description:
    `${label} to keep: ids of \`facets.${filter}\` options. Repeat the parameter per id, up to 50; ` +
    'ids past the fiftieth are ignored.',
});

export const customPackSearchParameters = [
  {
    name: 'q',
    in: 'query',
    required: false,
    schema: { type: 'string', maxLength: 100 },
    description: 'Free-text search over the cards. Only the first 100 characters are used.',
  },
  {
    name: 'category_id',
    in: 'query',
    required: false,
    schema: { type: 'integer' },
    description: 'Only cards of one category: an `id` of `facets.category`.',
  },
  filterIds('sport[]', 'sport', 'Sports'),
  filterIds('grader[]', 'grader', 'Grading companies'),
  filterIds('grade[]', 'grade', 'Grades'),
  filterIds('year[]', 'year', 'Years'),
  filterIds('set[]', 'set', 'Sets'),
  filterIds('pokemon_rarity[]', 'pokemon_rarity', 'Pokémon rarities'),
  ...customPackPriceParameters,
  {
    name: 'sort',
    in: 'query',
    required: false,
    schema: { type: 'string', enum: ['fmv_desc', 'fmv_asc'], default: 'fmv_desc' },
    description: 'Order by fair market value: highest first (`fmv_desc`) or lowest first (`fmv_asc`).',
  },
  {
    name: 'page',
    in: 'query',
    required: false,
    schema: { type: 'integer', default: 1, minimum: 1 },
    description:
      '1-based page number. Defaults to 1. Paging stops at the search window: `meta.has_more` is `false` once ' +
      '`(page + 1) × per` would exceed 10,000 results, and a page beyond the window answers `200` with an empty page.',
  },
  {
    name: 'per',
    in: 'query',
    required: false,
    schema: { type: 'integer', default: 24, maximum: 50 },
    description: 'Number of cards per page. Defaults to 24, capped at 50.',
  },
  {
    name: 'facets',
    in: 'query',
    required: false,
    schema: { type: 'integer', enum: [0] },
    description:
      'Send `0` to leave `facets` out of a page-1 response — when the search is narrowed and you already hold the ' +
      'options to offer. Leave it out of the request to get `facets` with page 1, as usual. Later pages never ' +
      'carry `facets`.',
  },
];

const facetOptions = (filter: string) => ({
  type: 'array',
  description: filter === 'category' ? 'Send an `id` as `category_id`.' : `Send an \`id\` as \`${filter}[]\`.`,
  items: {
    type: 'object',
    title: 'CustomPackFacetOption',
    required: ['id', 'label'],
    properties: {
      id: { type: 'integer', description: 'Option id — the value to send back as a filter.' },
      label: { type: 'string', description: 'Name to show the player.' },
    },
  },
});

export const customPackCardsSchema = {
  type: 'object',
  title: 'CustomPackCards',
  description:
    'A page of cards. `facets` and `price` come with page 1 only: a later page holds `items` and `meta` and nothing ' +
    'else. A missing `facets` or `price`, on any page, means "keep what you hold" — it is not an empty list.',
  required: ['items', 'meta'],
  properties: {
    items: {
      type: 'array',
      description:
        'The cards on this page, in the requested `sort` order. A page can hold fewer than `per` cards even when ' +
        '`meta.has_more` is `true`.',
      items: {
        type: 'object',
        title: 'CustomPackCard',
        required: ['id', 'title', 'fair_market_value_cents'],
        properties: {
          id: { type: 'integer', description: 'Card id — send it as `pool_item_id` in a selection.' },
          title: { type: 'string', description: 'Display name of the card.' },
          image_url: {
            type: 'string',
            format: 'uri',
            nullable: true,
            description: 'Card image. Null when no image is available.',
          },
          fair_market_value_cents: {
            type: 'integer',
            description: 'Current fair market value of the card, in cents of USD.',
          },
          grade: {
            type: 'object',
            nullable: true,
            title: 'CustomPackCardGrade',
            description: 'Grading company and grade. Null when the card carries no grade data.',
            properties: {
              company: { type: 'string', nullable: true, description: 'Grading company, e.g. "PSA".' },
              grade: { type: 'string', nullable: true, description: 'Grade, e.g. "10".' },
            },
          },
        },
      },
    },
    facets: {
      type: 'object',
      title: 'CustomPackFacets',
      description:
        'The filter options to offer next to the grid, each as `{ id, label }`. They carry no counts. Only page 1 ' +
        'carries `facets`, and not when the request sent `facets=0`: the key is absent then, so keep the options ' +
        'you hold.',
      required: ['category', 'sport', 'grader', 'grade', 'year', 'set', 'pokemon_rarity'],
      properties: {
        category: facetOptions('category'),
        sport: facetOptions('sport'),
        grader: facetOptions('grader'),
        grade: facetOptions('grade'),
        year: facetOptions('year'),
        set: facetOptions('set'),
        pokemon_rarity: facetOptions('pokemon_rarity'),
      },
    },
    price: customPackPriceSchema,
    meta: {
      type: 'object',
      title: 'CustomPackCardsMeta',
      description: 'Paging state. There is no total: keep requesting pages while `has_more` is `true`.',
      required: ['page', 'per', 'has_more'],
      properties: {
        page: { type: 'integer', description: 'The page returned.' },
        per: { type: 'integer', description: 'The page size in effect.' },
        has_more: {
          type: 'boolean',
          description:
            'Whether another page follows. `false` on the last page and at the search window — see `page`.',
        },
      },
    },
  },
};

export const customPackCardsExample = {
  items: [
    {
      id: 11,
      title: '1999 Pokémon Base Set Charizard #4 Holo',
      image_url: 'https://cdn.getcardbase.com/blobs/card_11.png',
      fair_market_value_cents: 100000,
      grade: { company: 'PSA', grade: '10' },
    },
    {
      id: 12,
      title: '2003 Topps Chrome LeBron James #111 Rookie',
      image_url: 'https://cdn.getcardbase.com/blobs/card_12.png',
      fair_market_value_cents: 30000,
      grade: { company: 'BGS', grade: '9.5' },
    },
  ],
  facets: {
    category: [{ id: 3, label: 'Pokemon' }],
    sport: [],
    grader: [{ id: 21, label: 'PSA' }],
    grade: [{ id: 35, label: '10' }],
    year: [],
    set: [],
    pokemon_rarity: [],
  },
  price: customPackPriceExample,
  meta: { page: 1, per: 24, has_more: true },
};

/** Page 1 and a later page: the later one has no `facets` and no `price`. */
export const customPackCardsExamples = {
  first_page: {
    summary: 'Page 1: the cards, the filter options and the price ranges',
    value: customPackCardsExample,
  },
  next_page: {
    summary: 'Page 2 and later: the cards and the paging state only',
    value: {
      items: [
        {
          id: 13,
          title: '2018 Panini Prizm Luka Doncic #280 Silver Rookie',
          image_url: 'https://cdn.getcardbase.com/blobs/card_13.png',
          fair_market_value_cents: 10000,
          grade: { company: 'PSA', grade: '9' },
        },
      ],
      meta: { page: 2, per: 24, has_more: false },
    },
  },
};

/** Response headers of a `429`. */
export const retryAfterHeaders = {
  'Retry-After': { description: 'Seconds to wait before the next request.', schema: { type: 'integer' } },
};
